/*
 * Slovenščina — the console's first language, and the SCHEMA the other one is checked
 * against: `en.ts` is `satisfies MessageSchema`, so a key missing from it (or one only it has)
 * fails `yarn type-check`. Plural messages carry the four Slovenian forms (1, 2, 3–4, 5+) in
 * that order; `libs/i18n/index.ts` picks the right one.
 */
const sl = {
    general: {
        add:           'Dodaj',
        edit:          'Uredi',
        delete:        'Izbriši',
        cancel:        'Prekliči',
        save:          'Shrani',
        close:         'Zapri',
        confirm:       'Potrdi',
        search:        'Išči …',
        loading:       'Nalagam …',
        saving:        'Shranjujem',
        clear_filters: 'Počisti filtre',
        sign_out:      'Odjava',
    },
    brand: {
        product_name: 'Atraktivna portal',
        tagline:      'Naloge',
    },
    nav: {
        group_naloge: 'Naloge',
        dashboard:   'Pregled',
        my_tasks:    'Moje naloge',
        projects:    'Projekti',
        timeline:    'Časovnica',
        group_admin: 'Skrbništvo',
        users:       'Uporabniki',
        settings:    'Nastavitve',
        profile:     'Profil',
    },
    layout: {
        open_menu:  'Odpri meni',
        close_menu: 'Zapri meni',
    },
    color_mode: {
        switch_to_light: 'Preklopi na svetli način',
        switch_to_dark:  'Preklopi na temni način',
    },
    module: {
        naloge:       'Naloge',
        role_manager: 'Vodja',
        role_member:  'Član',
        off:          'Izklopljeno',
        portal_admin: 'Skrbnik portala',
        none:         'Brez modulov',
        access:       'Dostop',
        access_help:  'Dostop ureja skrbnik portala.',
    },
    locale: {
        label: 'Jezik',
        sl:    'Slovenščina',
        en:    'English',
    },
    errors: {
        generic_error: 'Prišlo je do napake.',
        invalid_data:  'Podatki niso veljavni.',
        not_found:     'Ne obstaja (več).',
        unauthorized:  'Prijava je potekla. Prijavite se znova.',
        forbidden:     'Za to nimate dovoljenja.',
        on_add:        'Dodajanje ni uspelo.',
        on_edit:       'Shranjevanje ni uspelo.',
        on_delete:     'Brisanje ni uspelo.',
        on_load:       'Nalaganje ni uspelo.',
    },
    success: {
        add:    'Dodano.',
        edit:   'Shranjeno.',
        delete: 'Izbrisano.',
    },
    validation: {
        required: 'Obvezno polje.',
        email:    'Neveljaven e-poštni naslov.',
        too_long: 'Predolgo.',
        invalid:  'Neveljavna vrednost.',
    },
    date: {
        pick:  'Izberi datum',
        clear: 'Počisti datum',
    },
    auth: {
        username:          'Uporabniško ime',
        password:          'Geslo',
        password_show:     'Pokaži geslo',
        password_hide:     'Skrij geslo',
        sign_in:           'Prijava',
        or:                'ali',
        failed:            'Napačno uporabniško ime ali geslo.',
        too_many_attempts: 'Preveč neuspelih poskusov. Poskusite znova čez nekaj minut.',
    },
    passkey: {
        passkeys:        'Ključi za prijavo (passkeys)',
        subtitle:        'Prijava s prstnim odtisom, obrazom ali varnostnim ključem namesto gesla.',
        sign_in:         'Prijava s passkeyem',
        sign_in_failed:  'Prijava s passkeyem ni uspela.',
        add:             'Dodaj passkey',
        added:           'Passkey je dodan.',
        failed:          'Passkeya ni bilo mogoče dodati.',
        name:            'Ime',
        name_help:       'Po imenu boste ključ prepoznali na seznamu, npr. »Službeni prenosnik«.',
        default_name:    'Moja naprava',
        continue:        'Nadaljuj',
        created:         'Dodan',
        last_used:       'Zadnja uporaba',
        never_used:      'Še ni bil uporabljen',
        empty:           'Nimate še nobenega passkeya.',
        delete:          'Izbriši passkey',
        confirm_delete:  'Izbrišem passkey »{name}«? S to napravo se ne boste več mogli prijaviti brez gesla.',
        removed:         'Passkey je izbrisan.',
        delete_failed:   'Passkeya ni bilo mogoče izbrisati.',
        unavailable:     'Prijava s passkeyi na tem strežniku ni nastavljena.',
        unsupported:     'Ta brskalnik ne podpira passkeyev (potrebna je varna povezava HTTPS).',
    },
    password: {
        change:        'Spremeni geslo',
        current:       'Trenutno geslo',
        new:           'Novo geslo',
        repeat:        'Ponovite novo geslo',
        mismatch:      'Gesli se ne ujemata.',
        changed:       'Geslo je spremenjeno.',
        failed:        'Gesla ni bilo mogoče spremeniti.',
        wrong_current: 'Trenutno geslo ni pravilno.',
    },
    profile: {
        title:             'Moj profil',
        subtitle:          'Ime in naslov, ki ju vidijo sodelavci in na katerega prihaja pošta.',
        account:           'Račun',
        notify_email:      'Obvestila po e-pošti',
        notify_email_help: 'Pošta, ko vam kdo dodeli nalogo ali jo komentira, in dnevni opomnik za roke.',
        locale_help:       'Jezik aplikacije in vaše e-pošte.',
    },
    user: {
        users:                'Uporabniki',
        subtitle:             'Kdo se lahko prijavi v portal in kaj lahko uporablja.',
        add:                  'Nov uporabnik',
        edit:                 'Uredi uporabnika',
        delete:               'Izbriši uporabnika',
        name:                 'Ime in priimek',
        username:             'Uporabniško ime',
        username_rule:        '3–32 znakov: male črke, številke, pika, podčrtaj ali vezaj.',
        username_fixed:       'Uporabniškega imena ni mogoče spremeniti.',
        username_taken:       'To uporabniško ime že obstaja.',
        email:                'E-pošta',
        portal_admin_help:    'Uporabniki in nastavitve portala. V modulih sam po sebi ne dovoli ničesar.',
        portal_admin_yes:     'Da',
        module_naloge_help:   'Vodja vidi in ureja vse projekte, član le svoje.',
        capacity:             'Zmogljivost',
        capacity_help:        'Ur dela na dan, s katerimi računa časovnica.',
        capacity_value:       '{hours} h/dan',
        capacity_range:       'Med 0,5 in 24 ur.',
        password:             'Geslo',
        password_reset:       'Novo geslo (ponastavitev)',
        password_keep:        'Pustite prazno, da geslo ostane nespremenjeno.',
        password_rule:        'Najmanj 8 znakov.',
        password_too_short:   'Geslo mora imeti najmanj 8 znakov.',
        active:               'Aktiven',
        active_help:          'Neaktiven uporabnik se ne more prijaviti in ga ni med izvajalci.',
        status:               'Stanje',
        status_active:        'Aktiven',
        status_inactive:      'Neaktiven',
        last_login:           'Zadnja prijava',
        never_logged_in:      'Še nikoli',
        search:               'Išči po imenu ali e-pošti …',
        show_inactive:        'Pokaži neaktivne',
        empty:                'Ni uporabnikov.',
        you:                  'Vi',
        cannot_delete_self:   'Svojega računa ne morete izbrisati.',
        confirm_delete:       'Izbrišem uporabnika {name}? Njegove naloge ostanejo in obdržijo izvajalca.',
        deleted:              'Uporabnik je izbrisan.',
        delete_failed:        'Uporabnika ni bilo mogoče izbrisati.',
        last_admin:           'To je zadnji aktivni skrbnik portala — ostati mora vsaj eden.',
        locale:               'Jezik',
        locale_help:          'Jezik aplikacije in e-pošte za tega uporabnika.',
    },
    mail: {
        title:                       'E-pošta',
        subtitle:                    'Strežnik, prek katerega gredo obvestila in dnevni opomniki.',
        enabled:                     'Pošiljanje e-pošte',
        enabled_help:                'Izklopljeno: obvestila so le v aplikaciji.',
        host:                        'Strežnik SMTP',
        host_placeholder:            'smtp.example.com',
        port:                        'Vrata',
        tls:                         'Šifriranje',
        tls_starttls:                'STARTTLS',
        tls_tls:                     'TLS',
        tls_none:                    'Brez',
        username:                    'Uporabniško ime',
        username_help:               'Prazno, če strežnik prijave ne zahteva.',
        password:                    'Geslo',
        password_help:               'Shrani se šifrirano in se nikoli ne prikaže.',
        password_stored:             'Geslo je shranjeno. Prazno polje ga ohrani, vpisano ga zamenja.',
        password_stored_placeholder: '••••••••',
        from_address:                'Naslov pošiljatelja',
        from_address_placeholder:    "naloge{'@'}atraktivna.si",
        from_name:                   'Ime pošiljatelja',
        base_url:                    'Naslov aplikacije',
        base_url_help:               'Povezave v e-pošti vodijo sem, npr. https://tasks.atraktivna.dev.it-team.dev.',
        base_url_invalid:            'Naslov se mora začeti s http:// ali https://.',
        reminder_hour:               'Ura dnevnega opomnika',
        reminder_hour_help:          'Ob tej uri (po ljubljanskem času) gre opomnik za roke jutri in zamude.',
        required_when_enabled:       'Obvezno, ko je pošiljanje vklopljeno.',
        test_title:                  'Preizkusno sporočilo',
        test_subtitle:               'Pošlje se prek shranjenih nastavitev — najprej shranite spremembe.',
        test_to:                     'Prejemnik',
        test_send:                   'Pošlji preizkus',
        test_sent:                   'Preizkusno sporočilo je poslano na {to}.',
        not_configured:              'E-pošta ni nastavljena ali ni vklopljena.',
        failed:                      'Sporočila ni bilo mogoče poslati.',
        failed_reply:                'Strežnik SMTP je zavrnil sporočilo: {reply}',
    },
    notification: {
        title:         'Obvestila',
        title_unread:  'Obvestila (neprebrana: {count})',
        mark_all_read: 'Označi vse kot prebrano',
        mark_failed:   'Obvestil ni bilo mogoče označiti.',
        empty:         'Ni obvestil.',
        unread:        'Neprebrano',
        assigned:      'Dodeljena vam je naloga',
        commented:     'Nov komentar pri nalogi',
        due_soon:      'Jutri zapade naloga',
        overdue:       'V zamudi je naloga',
    },
    activity: {
        empty:                'Še ni aktivnosti.',
        system:               'Sistem',
        load_more:            'Naloži starejše',
        task_created:         'Nova naloga',
        task_updated:         'Posodobljena naloga',
        status_changed:       'Status: {from} → {to}',
        assigned:             'Izvajalec: {from} → {to}',
        schedule_changed:     'Termin: {from} → {to}',
        cascade_from:         '(zaradi {key})',
        dependencies_changed: 'Spremenjene odvisnosti',
        comment_added:        'Nov komentar',
        attachment_added:     'Nova priponka',
        attachment_removed:   'Odstranjena priponka',
        task_deleted:         'Izbrisana naloga',
        project_created:      'Nov projekt',
        project_updated:      'Posodobljen projekt',
        members_changed:      'Spremenjeni člani projekta',
    },
    schedule: {
        shifted_title: 'Premaknjena je {n} odvisna naloga | Premaknjeni sta {n} odvisni nalogi | Premaknjene so {n} odvisne naloge | Premaknjenih je {n} odvisnih nalog',
    },
    dashboard: {
        stat_open:     'Odprte naloge',
        stat_overdue:  'V zamudi',
        stat_today:    'Danes',
        stat_week:     'Ta teden',
        my_tasks:      'Moje naloge',
        all_my_tasks:  'Vse moje naloge',
        today_is:      'Danes je {day}',
        group_overdue: 'Zamuda',
        group_today:   'Danes',
        group_week:    'Ta teden',
        nothing_due:   'Ta teden nimate nobenega roka. Lepo!',
        projects:      'Projekti',
        all_projects:  'Vsi projekti',
        activity:      'Zadnja aktivnost',
    },
    my_tasks: {
        title:         'Moje naloge',
        subtitle:      'Naloge, ki so dodeljene vam, po rokih.',
        scope_open:    'Odprte',
        scope_all:     'Vse',
        empty:         'Nimate dodeljenih nalog.',
        group_overdue: 'Zamuda',
        group_today:   'Danes',
        group_week:    'Ta teden',
        group_later:   'Kasneje',
        group_none:    'Brez roka',
        group_done:    'Končano',
    },
    project: {
        projects:               'Projekti',
        subtitle:               'Projekti, ki jih lahko vidite.',
        search:                 'Išči po ključu, imenu ali vodji …',
        empty:                  'Ni projektov.',
        add:                    'Nov projekt',
        edit:                   'Uredi projekt',
        delete:                 'Izbriši projekt',
        deleted:                'Projekt je izbrisan.',
        confirm_delete:         'Izbrišem projekt {key} {name}? Z njim izginejo vse njegove naloge.',
        key:                    'Ključ',
        key_help:               '2–6 znakov, npr. NAB.',
        key_rule:               '2–6 velikih črk ali številk, prvi znak je črka.',
        key_taken:              'Ta ključ že uporablja drug projekt.',
        name:                   'Ime',
        description:            'Opis',
        status:                 'Stanje',
        status_filter:          'Stanje projektov',
        status_active:          'Aktiven',
        status_on_hold:         'Na čakanju',
        status_done:            'Zaključen',
        status_archived:        'Arhiviran',
        filter_current:         'Vsi razen arhiviranih',
        filter_all:             'Vsi, tudi arhivirani',
        lead:                   'Vodja projekta',
        lead_help:              'Vodja lahko ureja projekt in njegove člane.',
        lead_me:                'Jaz (privzeto)',
        lead_none:              'Brez vodje',
        start_date:             'Začetek',
        due_date:               'Rok',
        dates:                  'Termin',
        members:                'Člani',
        members_help:           'Samo člani lahko prevzamejo naloge projekta. Vodja je član samodejno.',
        members_placeholder:    'Izberite sodelavce …',
        members_readonly:       'Člane lahko ureja skrbnik, vodja ali vodja projekta.',
        members_unsaved:        'Spremembe članov še niso shranjene.',
        members_saved:          'Člani so shranjeni.',
        members_failed:         'Članov ni bilo mogoče shraniti.',
        add_member:             'Dodaj',
        add_member_placeholder: 'Dodaj sodelavca …',
        remove_member:          'Odstrani {name}',
        role:                   'Vloga v projektu',
        role_lead:              'Vodja projekta',
        role_member:            'Član',
        tasks:                  'Naloge',
        progress:               'Napredek',
        progress_of:            '{done}/{total} končanih',
        in_progress_count:      '{count} v delu',
        overdue_count:          '{n} v zamudi | {n} v zamudi | {n} v zamudi | {n} v zamudi',
        view_list:              'Seznam',
        view_kanban:            'Kanban',
        view_gantt:             'Gantt',
        view_members:           'Člani',
        view_activity:          'Aktivnost',
        forbidden:              'Tega projekta ne morete videti.',
        not_found:              'Projekt ne obstaja.',
        unavailable_help:       'Morda je bil izbrisan ali pa niste med njegovimi člani. Za dostop prosite vodjo projekta.',
        back_to_projects:       'Nazaj na projekte',
    },
    task: {
        task:                      'Naloga',
        key:                       'Ključ',
        title:                     'Naslov',
        description:               'Opis',
        description_placeholder:   'Kaj je treba narediti, kje so podatki …',
        status:                    'Status',
        status_todo:               'Za narediti',
        status_in_progress:        'V delu',
        status_review:             'V pregledu',
        status_done:               'Končano',
        status_set:                '{key}: {status}',
        priority:                  'Prioriteta',
        priority_low:              'Nizka',
        priority_normal:           'Običajna',
        priority_high:             'Visoka',
        priority_urgent:           'Nujna',
        assignee:                  'Izvajalec',
        assignee_help:             'Nalogo lahko prevzame le član projekta.',
        assignee_not_member:       'Izvajalec mora biti član projekta.',
        assignee_not_member_label: '{name} (ni več član)',
        unassigned:                'Nedodeljeno',
        all_statuses:              'Vsi statusi',
        all_assignees:             'Vsi izvajalci',
        start_date:                'Začetek',
        due_date:                  'Rok',
        start_short:               'Začetek',
        due_short:                 'Rok',
        milestone:                 'Mejnik',
        milestone_help:            'En dan brez trajanja — na Ganttu je romb.',
        milestone_date:            'Dan mejnika',
        milestone_needs_date:      'Mejnik potrebuje datum.',
        due_before_start:          'Rok ne more biti pred začetkom.',
        no_dates:                  'brez datumov',
        estimate:                  'Ocena (ure)',
        estimate_help:             'Porazdeli se po delovnih dneh na časovnici.',
        estimate_none:             'Brez ocene',
        estimate_range:            'Med 0 in 10.000 ur.',
        progress:                  'Napredek',
        progress_value:            'Napredek: {value} %',
        overdue:                   'V zamudi',
        blocked:                   'Blokirana',
        blocked_help:              'Čaka na predhodno nalogo, ki še ni končana.',
        predecessors:              'Predhodne naloge',
        predecessors_help:         'Naloga se lahko začne šele dan po koncu vseh predhodnih (iz istega projekta).',
        predecessors_none:         'Brez predhodnih nalog',
        successors:                'Naslednje naloge',
        successors_none:           'Nobena naloga ne čaka na to.',
        dependency_cycle:          'Ta odvisnost bi ustvarila krog — naloga ne more čakati sama nase.',
        dependency_other_project:  'Predhodna naloga mora biti iz istega projekta.',
        tab_details:               'Podrobnosti',
        tab_comments:              'Komentarji',
        tab_attachments:           'Priponke',
        tab_activity:              'Aktivnost',
        created_meta:              'Ustvarjeno',
        completed:                 'Končano',
        new_placeholder:           'Nova naloga — vpišite naslov in pritisnite Enter',
        add:                       'Dodaj nalogo',
        created:                   'Naloga je dodana.',
        create_failed:             'Naloge ni bilo mogoče dodati.',
        search:                    'Išči po nalogah …',
        count:                     '{n} naloga | {n} nalogi | {n} naloge | {n} nalog',
        empty_filtered:            'Ni nalog, ki bi ustrezale filtrom.',
        saved:                     'Naloga je shranjena.',
        save_failed:               'Naloge ni bilo mogoče shraniti.',
        nothing_changed:           'Ni sprememb.',
        delete:                    'Izbriši nalogo',
        confirm_delete:            'Izbrišem nalogo {key} {title}? Odstrani se tudi iz odvisnosti drugih nalog.',
        deleted:                   'Naloga je izbrisana.',
        delete_failed:             'Naloge ni bilo mogoče izbrisati.',
        not_available:             'Te naloge ne morete odpreti — morda je bila izbrisana ali pa niste član projekta.',
        load_failed:               'Naloge ni bilo mogoče naložiti.',
    },
    comment: {
        empty:          'Še ni komentarjev.',
        placeholder:    'Napišite komentar …',
        shortcut:       '⌘/Ctrl + Enter pošlje',
        add:            'Komentiraj',
        add_failed:     'Komentarja ni bilo mogoče dodati.',
        edited:         'urejeno',
        edit_failed:    'Komentarja ni bilo mogoče shraniti.',
        delete:         'Izbriši komentar',
        confirm_delete: 'Izbrišem ta komentar?',
        deleted:        'Komentar je izbrisan.',
        delete_failed:  'Komentarja ni bilo mogoče izbrisati.',
        load_failed:    'Komentarjev ni bilo mogoče naložiti.',
    },
    attachment: {
        help:                'Datoteke do 25 MB.',
        upload:              'Naloži datoteko',
        uploaded:            'Naložena je {n} datoteka | Naloženi sta {n} datoteki | Naložene so {n} datoteke | Naloženih je {n} datotek',
        upload_failed_named: 'Datoteke »{name}« ni bilo mogoče naložiti.',
        too_large:           'Datoteka je večja od 25 MB.',
        too_large_named:     'Datoteka »{name}« je večja od 25 MB.',
        empty:               'Ni priponk.',
        download:            'Prenesi',
        delete:              'Izbriši priponko',
        confirm_delete:      'Izbrišem priponko »{name}«?',
        deleted:             'Priponka je izbrisana.',
        delete_failed:       'Priponke ni bilo mogoče izbrisati.',
        load_failed:         'Priponk ni bilo mogoče naložiti.',
    },
    kanban: {
        open:         'Odpri nalogo',
        move_to:      'Premakni v',
        card_menu:    'Možnosti za {key}',
        add_to:       'Dodaj nalogo v »{column}«',
        new_title:    'Naslov nove naloge',
        empty_column: 'Ni nalog',
        move_failed:  'Naloge ni bilo mogoče premakniti.',
        hint_mouse:   'Kartico povlecite v drug stolpec ali drugo mesto; klik jo odpre.',
        hint_touch:   'Kartico držite in povlecite; dotik jo odpre.',
    },
    gantt: {
        task:        'Naloga',
        today:       'Danes',
        zoom_day:    'Dan',
        zoom_week:   'Teden',
        zoom_month:  'Mesec',
        empty:       'Nobena naloga še nima datuma. Določite začetek ali rok v nalogi.',
        undated:     'Brez datumov',
        moved:       '{key}: {range}',
        move_failed: 'Termina ni bilo mogoče spremeniti.',
        hint_mouse:  'Povlecite trak, da ga premaknete, ali njegov rob, da spremenite začetek ali rok.',
        hint_touch:  'Trak držite in povlecite; dotik odpre nalogo.',
    },
    timeline: {
        title:             'Kdo kaj do kdaj',
        subtitle:          'Naloge vseh ljudi na enem koledarju in njihova obremenitev po dnevih.',
        window:            'Obdobje',
        preset_default:    'Teden nazaj, 8 tednov naprej',
        preset_this_week:  'Ta teden',
        preset_two_weeks:  'Naslednja 2 tedna',
        preset_this_month: 'Ta mesec',
        preset_quarter:    'Naslednji 3 meseci',
        preset_custom:     'Po meri …',
        from:              'Od',
        to:                'Do',
        project:           'Projekt',
        all_projects:      'Vsi projekti',
        people:            'Ljudje',
        all_people:        'Vsi ljudje',
        include_done:      'Tudi končane',
        person:            'Oseba',
        unassigned:        'Nedodeljeno',
        task_count:        '{n} naloga | {n} nalogi | {n} naloge | {n} nalog',
        capacity:          '{hours} h/dan',
        overloaded_days:   '{n} preobremenjen dan | {n} preobremenjena dneva | {n} preobremenjeni dnevi | {n} preobremenjenih dni',
        load_tooltip:      '{day}: {hours} h od {capacity} h',
        estimate:          'ocena {hours} h',
        legend_load:       'Obremenitev',
        legend_overload:   'Preobremenitev',
        empty:             'V tem obdobju ni nalog z datumi.',
    },
}

export type MessageSchema = typeof sl

export default sl
